import React, { useEffect, FC, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validateLoanAmount } from '@qred/shared-component-library/src/validators';
import {
  getDefaultValueOrMinValue,
  getDefaultLoanAmountValuesOrInputValues,
  dropDownOptions,
  formatSliderValue,
} from './LoanAmountService';
import { setInput, validateValue } from '~/actions/formActions';
import { setInvalidError, setMissingError } from '~/actions/errorActions';
import { LOAN_AMOUNT } from '~/actions/actionTypes';
import { IState } from '~/sharedInterfaces/IState';
import { getCurrency } from '~/utils';
import Input from '../Base/Input/Input';
import Slider from '../Base/Slider/Slider';
import DropDown from '../Base/DropDown/DropDown';
import { IBaseInput, IDropDownOption, ISliderValues } from '~/sharedInterfaces/SharedInterfaces';
import { IBaseComponent } from '~/sharedInterfaces/IBaseComponent';

export enum LoanAmountComponentTypes {
  DROPDOWN = 'dropDown',
  SLIDER = 'slider',
}

export interface ILoanAmount extends IBaseInput {
  componentType?: LoanAmountComponentTypes;
  sliderValues?: ISliderValues;
  dropDownValues?: IDropDownOption[];
}

interface ILoanAmountProps extends ILoanAmount, IBaseComponent {}

const LoanAmount: FC<ILoanAmountProps> = (props) => {
  const dispatch = useDispatch();
  const { market } = props.settings;

  const value = useSelector((state: IState) => state.signup.loanAmount || '');
  const error = useSelector((state: IState) => state.error.loanAmount);
  const { locale } = useSelector((state: IState) => state.widgetContext);

  const sliderValues = getDefaultLoanAmountValuesOrInputValues(market, props.sliderValues);
  const unit = getCurrency(market);

  const handleOnChange = (event: ChangeEvent<any>) => {
    const inputValue = event.target.value;
    dispatch(setInput(LOAN_AMOUNT, inputValue));
    if (error?.missing) dispatch(setMissingError(LOAN_AMOUNT, ''));
  };

  const handleValidation = () => {
    dispatch(validateValue(validateLoanAmount, LOAN_AMOUNT, value, locale));
  };

  const defaultValues = {
    ...props,
    value,
    error: error?.missing || error?.invalid,
    formatValueAmount: formatSliderValue(market, value, sliderValues).amount,
    formatValueUnit: formatSliderValue(market, value, sliderValues).unit,
    onChange: handleOnChange,
    analyticsEvent: 'loan-amount',
    handleValidation,
  };

  useEffect(() => {
    const validLoanAmount = validateLoanAmount(value, market);
    if (validLoanAmount && error?.invalid) dispatch(setInvalidError(LOAN_AMOUNT, ''));
  }, [value]);

  useEffect(() => {
    if (
      !defaultValues.value &&
      (props.componentType === LoanAmountComponentTypes.SLIDER ||
        props.componentType === LoanAmountComponentTypes.DROPDOWN)
    ) {
      dispatch(
        setInput(props.componentName, getDefaultValueOrMinValue(market, props.sliderValues))
      );
    }
  }, []);

  switch (props.componentType) {
    case LoanAmountComponentTypes.SLIDER:
      return <Slider {...defaultValues} sliderValues={sliderValues} />;
    case LoanAmountComponentTypes.DROPDOWN:
      return (
        <DropDown
          {...defaultValues}
          dropDownValues={
            props.dropDownValues && props.dropDownValues.length > 0
              ? props.dropDownValues
              : dropDownOptions(market)
          }
        />
      );
    default:
      return (
        <Input {...defaultValues} inputMode="numeric">
          <p className="qred-first">
            <span className="qred-first">{unit}</span>
          </p>
        </Input>
      );
  }
};

export default LoanAmount;
